import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { UserDropdown } from '../components/UserDropdown';
import { databaseService } from '../services/database';
import type { Modelo, Location } from '../services/api';
import { verifySmartProximityWithBrazilFallback } from '../services/smart-proximity';
import { getCurrentPosition, checkLocationServiceStatus } from '../services/geolocation';
import { getDeviceInfo, getLocationWithStrategies } from '../services/deviceLocation';
import Card from '../components/Card';
import { CheckIcon, XIcon, MaskIcon, Loading } from '../components';
import { ENV_CONFIG, isDevEnvironment, isDebugMode } from '../config/env';
import { logger } from '../utils/logger';
import { convertISO88591ToUTF8 } from '../hooks/useEncodingConverter';

export default function Home() {
  const { user } = useAuth();

  const [address, setAddress] = useState('');
  const [notificado, setNotificado] = useState('');
  const [protocolo, setProtocolo] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);

  const [availableLocations, setAvailableLocations] = useState<Location[]>([]);
  const [addressesLoaded, setAddressesLoaded] = useState(false);

  const [statusFilter, setStatusFilter] = useState<'POSITIVO' | 'NEGATIVO' | ''>('');
  const [selectedModelo, setSelectedModelo] = useState<string>('');
  const [availableModelos, setAvailableModelos] = useState<Modelo[]>([]);
  const [modelosLoaded, setModelosLoaded] = useState(false);

  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [deviceInfo] = useState(() => getDeviceInfo());

  const [simulationMode, setSimulationMode] = useState(isDevEnvironment());

  useEffect(() => {
    loadUserAddresses();
    loadModelos();
  }, [user]);

  useEffect(() => {
    if (!address.trim()) {
      setStatusFilter('');
      setSelectedModelo('');
      setNotificado('');
      setProtocolo('');
      setSelectedLocationId(null);
    } else {
      // Parse do valor selecionado para extrair o id_location
      const locationId = parseInt(address);
      const selectedLocation = availableLocations.find(loc => loc.id_location === locationId);
      if (selectedLocation) {
        setNotificado(selectedLocation.notificado || '');
        setProtocolo(selectedLocation.protocolo || '');
        setSelectedLocationId(selectedLocation.id_location);
      } else {
        setNotificado('');
        setProtocolo('');
        setSelectedLocationId(null);
      }
    }
  }, [address, availableLocations]);

  const loadUserAddresses = async () => {
    if (!user) {
      setAvailableLocations([]);
      setAddressesLoaded(true);
      return;
    }

    try {
      // Buscar todas as locations do usuário usando filtro por id_notificador
      const locationsData = await databaseService.getLocations(user.id_notificador);
      const sortedLocationsData = locationsData.sort((a, b) => a.endereco.localeCompare(b.endereco));
      if (isDebugMode()) {
        logger.log('[DEBUG] Dados do usuário logado:', {
          id_notificador: user.id_notificador,
          notificador: user.notificador,
          documento: user.documento
        });

        logger.log('[DEBUG] Total de locations no banco:', sortedLocationsData?.length || 0);

        logger.log('[DEBUG] Primeiras 3 locations do banco:',
          sortedLocationsData?.slice(0, 3).map(loc => ({
            id_location: loc.id_location,
            id_notificador: loc.id_notificador,
            endereco: loc.endereco,
            notificado: loc.notificado
          }))
        );
      }

      const userLocations = (locationsData || []).filter(loc =>
        loc.id_notificador === user.id_notificador
      );

      console.log(userLocations)

      // Não precisamos mais do array separado de endereços
      setAvailableLocations(userLocations);
      setAddressesLoaded(true);
      console.log('Endereços carregados:', userLocations.length);
      console.log('Endereços disponíveis:', JSON.stringify(userLocations.map(loc => loc.endereco), null, 2));

      // Debug adicional para entender a estrutura
      if (userLocations.length > 0) {
        console.log('Exemplo de location carregada:', userLocations[0]);
        console.log('Campos da location:', Object.keys(userLocations[0]));
      }
    } catch (error) {
      console.error('Erro ao carregar endereços:', error);
      setAvailableLocations([]);
      setAddressesLoaded(true);
    }
  };

  const loadModelos = async () => {
    setModelosLoaded(false);
    try {
      const modelosData = await databaseService.getModelos();
      setAvailableModelos(modelosData || []);
      setModelosLoaded(true);
    } catch (error) {
      console.error('Erro ao carregar modelos:', error);
      setAvailableModelos([]);
      setModelosLoaded(true);
    }
  };

  // Filtrar modelos baseado no status selecionado
  // Debug: Exibir modelos disponíveis e seus id_status
  console.log('Modelos disponíveis:', availableModelos.map(m => ({ id_modelo: m.id_modelo, id_status: m.id_status, modelo: m.modelo })));

  const filteredModelos = availableModelos.filter(modelo => {
    if (!statusFilter) return true;
    const statusId = Number(modelo.id_status);
    if (statusFilter === 'NEGATIVO') {
      return statusId === 1;
    } else if (statusFilter === 'POSITIVO') {
      return statusId !== 1;
    }
    return true;
  });

  const createDiligencia = async (
    locationId: number,
    enderecoCoords: string,
    dispositivoCoords: string,
    dentroDoRaio: boolean,
    modeloId: string
  ): Promise<boolean> => {
    if (!user || !user.documento) return false;

    try {
      console.log('[DILIGENCIA] Iniciando criação de diligência...');
      console.log('[DILIGENCIA] Location ID:', locationId);
      console.log('[DILIGENCIA] Usuário ID:', user.id_notificador);

      // Busca a location específica pelo ID
      const selectedLocation = availableLocations.find(loc => loc.id_location === locationId);

      if (!selectedLocation?.id_location) {
        console.error('[ERROR] Location não encontrada na lista carregada');
        console.log('[DEBUG] Locations disponíveis:', availableLocations.map(loc => ({ id: loc.id_location, endereco: loc.endereco })));
        return false;
      }

      console.log('[DILIGENCIA] Location encontrada:', {
        id_location: selectedLocation.id_location,
        endereco: selectedLocation.endereco,
        notificado: selectedLocation.notificado
      });

      // Usa o id_location diretamente para buscar dados atualizados (se necessário)
      // Isso garante que temos os dados mais recentes da location específica
      let userLocation = selectedLocation;

      // Validação dos dados necessários
      const now = new Date();
      const currentDate = now.toLocaleDateString('pt-BR').split('/').reverse().join('-');
      const currentTime = now.toLocaleTimeString('pt-BR', { hour12: false });

      const modeloIdNum = modeloId && modeloId.trim() !== '' ? parseInt(modeloId) : null;

      if (!modeloIdNum || isNaN(modeloIdNum)) {
        console.error('[ERROR] ID do modelo inválido:', modeloId);
        return false;
      }

      // Criar a diligência usando o id_location específico
      const diligenciaData = {
        id_location: userLocation.id_location,
        id_modelo: modeloIdNum,
        endereco_coordenada: enderecoCoords,
        dispositivo_coordenada: dispositivoCoords,
        dentrodoraio: dentroDoRaio,
        data: currentDate,
        hora: currentTime
      };

      console.log('[DILIGENCIA] Dados para criar diligência:', diligenciaData);

      const response = await databaseService.createDiligencia(diligenciaData);

      if (response?.success) {
        if (isDebugMode()) {
          logger.log('[SUCCESS] Diligência criada com sucesso!', {
            id_deligencia: response.data?.id_deligencia,
            endereco: selectedLocation.endereco,
            modelo: modeloIdNum,
            dentrodoraio: dentroDoRaio,
            data: currentDate,
            hora: currentTime
          });
        }
        return true;
      } else {
        console.error('[ERROR] Falha ao criar diligência:', response?.error);
        return false;
      }
    } catch (error) {
      console.error('[ERROR] Erro ao criar diligência:', error);
      return false;
    }
  };

  const handleVerify = async () => {
    if (!address.trim() || !selectedLocationId) {
      setError('Por favor, selecione um endereço válido');
      return;
    }

    const selectedLocation = availableLocations.find(loc => loc.id_location === selectedLocationId);
    if (!selectedLocation) {
      setError('Endereço selecionado não encontrado');
      return;
    }

    if (availableLocations.length === 0) {
      setError('Todos os endereços já foram confirmados');
      return;
    }

    if (!selectedModelo) {
      setError('Por favor, selecione um modelo');
      return;
    }

    if (isDebugMode()) {
      logger.log('Starting verification in', ENV_CONFIG.APP_ENV, 'mode');
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // NOVA VERIFICAÇÃO: Verifica se o GPS está funcionando antes de prosseguir
      if (!simulationMode) {
        logger.log('🔍 Verificando status do GPS...');
        console.log('🔍 [DEBUG] Verificando status do GPS...'); // Log adicional sempre visível

        try {
          const locationStatus = await checkLocationServiceStatus();
          logger.log('📍 Status do GPS:', locationStatus);
          console.log('📍 [DEBUG] Status do GPS:', locationStatus); // Log adicional sempre visível

          if (!locationStatus.isSupported || !locationStatus.hasPermission || !locationStatus.isGPSEnabled) {
            const errorMsg = locationStatus.errorMessage || 'Serviço de localização não está disponível';
            logger.error('❌ GPS não disponível:', errorMsg);
            console.error('❌ [DEBUG] GPS não disponível:', errorMsg); // Log adicional sempre visível
            setError(errorMsg);
            setLoading(false);
            return;
          }

          logger.log('✅ GPS verificado com sucesso');
          console.log('✅ [DEBUG] GPS verificado com sucesso'); // Log adicional sempre visível
        } catch (gpsError: any) {
          logger.error('❌ Erro na verificação do GPS:', gpsError);
          console.error('❌ [DEBUG] Erro na verificação do GPS:', gpsError); // Log adicional sempre visível
          setError('Erro ao verificar GPS: ' + (gpsError.message || 'Erro desconhecido'));
          setLoading(false);
          return;
        }
      }

      // Usa estratégia híbrida para geocodificação inteligente
      logger.log('🧠 Iniciando verificação híbrida inteligente');
      console.log('🧠 [DEBUG] Iniciando verificação híbrida inteligente');

      // Converte Location da API para Location dos types
      const compatibleLocation = {
        id_location: selectedLocation.id_location,
        id_nome: selectedLocation.id_nome,
        id_notificador: selectedLocation.id_notificador,
        id_endereco: selectedLocation.id_endereco,
        protocolo: selectedLocation.protocolo || '',
        endereco: selectedLocation.endereco,
        notificado: selectedLocation.notificado,
        cep: selectedLocation.endereco.match(/\d{5}-?\d{3}/)?.[0] || '' // Extrai CEP do endereço
      };

      if (simulationMode) {
        // Usa geocodificação com fallback brasileiro no modo simulação
        const proximityResult = await verifySmartProximityWithBrazilFallback(
          // Coordenadas fictícias próximas para simulação
          {
            latitude: -23.550520 + (Math.random() - 0.5) * 0.01,
            longitude: -46.633308 + (Math.random() - 0.5) * 0.01
          },
          compatibleLocation
        );

        const inside = proximityResult.isWithinTarget;
        setResult(proximityResult.message);

        // Log detalhado do resultado híbrido
        logger.log('🎯 Resultado da verificação híbrida:', {
          method: proximityResult.method,
          precision: proximityResult.precision,
          result: inside ? 'DENTRO' : 'FORA',
          distance: proximityResult.distance,
          geocodingSource: proximityResult.geocodingResult.source
        });

        // Criar diligência e verificar se foi bem-sucedida
        const enderecoCoords = `${proximityResult.coords.target.latitude.toFixed(6)}, ${proximityResult.coords.target.longitude.toFixed(6)}`;
        const dispositivoCoords = `${proximityResult.coords.user.latitude.toFixed(6)}, ${proximityResult.coords.user.longitude.toFixed(6)}`;
        const apiSuccess = await createDiligencia(selectedLocationId!, enderecoCoords, dispositivoCoords, inside, selectedModelo);

        if (!apiSuccess) {
          setError('Erro ao criar diligência');
          return;
        }

        // Recarregar endereços disponíveis após sucesso
        await loadUserAddresses();

        // Atualização otimista: remove a location da lista imediatamente
        setAvailableLocations(prev => {
          const updated = prev.filter(loc => loc.id_location !== selectedLocationId);
          if (isDebugMode()) {
            logger.log(`[UPDATE] Removendo location ID ${selectedLocationId} da lista. Antes: ${prev.length}, Depois: ${updated.length}`);
          }
          return updated;
        });

        // Limpar seleção
        setAddress('');
        setNotificado('');
        setProtocolo('');
        setSelectedLocationId(null);

        // Segundo reload com delay como backup (se necessário)
        setTimeout(async () => {
          if (isDebugMode()) {
            logger.log('[RELOAD] Fazendo reload adicional após 1s...');
          }
          await loadUserAddresses();
        }, 1000);

        logger.log('Verificação simulada concluída!');
      } else {
        // Usa estratégia híbrida com GPS real
        try {
          const result = await getLocationWithStrategies();
          const { latitude, longitude } = result;

          // Verifica proximidade usando estratégia com fallback brasileiro
          const proximityResult = await verifySmartProximityWithBrazilFallback(
            { latitude, longitude },
            compatibleLocation
          );

          const inside = proximityResult.isWithinTarget;
          setResult(proximityResult.message);

          // Log detalhado do resultado híbrido
          logger.log('🎯 Resultado da verificação híbrida (GPS real):', {
            method: proximityResult.method,
            precision: proximityResult.precision,
            result: inside ? 'DENTRO' : 'FORA',
            distance: proximityResult.distance,
            geocodingSource: proximityResult.geocodingResult.source,
            gpsMethod: result.method
          });

          // Criar diligência e verificar se foi bem-sucedida
          const enderecoCoords = `${proximityResult.coords.target.latitude.toFixed(6)}, ${proximityResult.coords.target.longitude.toFixed(6)}`;
          const dispositivoCoords = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
          const apiSuccess = await createDiligencia(selectedLocationId!, enderecoCoords, dispositivoCoords, inside, selectedModelo);

          if (!apiSuccess) {
            setError('Erro ao criar diligência');
            return;
          }

          // Recarregar endereços disponíveis após sucesso
          await loadUserAddresses();

          // Atualização otimista: remove a location da lista imediatamente
          setAvailableLocations(prev => {
            const updated = prev.filter(loc => loc.id_location !== selectedLocationId);
            if (isDebugMode()) {
              logger.log(`[UPDATE] Removendo location ID ${selectedLocationId} da lista. Antes: ${prev.length}, Depois: ${updated.length}`);
            }
            return updated;
          });

          // Limpar seleção
          setAddress('');
          setNotificado('');
          setProtocolo('');
          setSelectedLocationId(null);

          // Segundo reload com delay como backup (se necessário)
          setTimeout(async () => {
            if (isDebugMode()) {
              logger.log('[RELOAD] Fazendo reload adicional após 1s...');
            }
            await loadUserAddresses();
          }, 1000);

          logger.log('Verificação concluída via', result.method);
        } catch (locationError: any) {
          // Se falhar com estratégias inteligentes, tenta o método tradicional com híbrido
          if (locationError.message === 'MANUAL_INPUT_REQUIRED') {
            setError('Localização automática não disponível. Tente novamente ou verifique suas configurações de localização.');
            return;
          }

          // Fallback para método tradicional + verificação híbrida
          logger.warn('GPS inteligente falhou, usando fallback com verificação híbrida');
          const position = await getCurrentPosition();
          const { latitude: fallbackLat, longitude: fallbackLon } = position.coords;

          // Verifica proximidade usando estratégia com fallback brasileiro
          const proximityResult = await verifySmartProximityWithBrazilFallback(
            { latitude: fallbackLat, longitude: fallbackLon },
            compatibleLocation
          );

          const inside = proximityResult.isWithinTarget;
          setResult(proximityResult.message);

          // Log detalhado do resultado híbrido
          logger.log('🎯 Resultado da verificação híbrida (fallback):', {
            method: proximityResult.method,
            precision: proximityResult.precision,
            result: inside ? 'DENTRO' : 'FORA',
            distance: proximityResult.distance,
            geocodingSource: proximityResult.geocodingResult.source
          });

          // Criar diligência e verificar se foi bem-sucedida
          const enderecoCoords = `${proximityResult.coords.target.latitude.toFixed(6)}, ${proximityResult.coords.target.longitude.toFixed(6)}`;
          const dispositivoCoords = `${fallbackLat.toFixed(6)}, ${fallbackLon.toFixed(6)}`;
          const apiSuccess = await createDiligencia(selectedLocationId!, enderecoCoords, dispositivoCoords, inside, selectedModelo);

          if (!apiSuccess) {
            setError('Erro ao criar diligência');
            return;
          }

          // Recarregar endereços disponíveis após sucesso
          await loadUserAddresses();

          // Atualização otimista: remove a location da lista imediatamente
          setAvailableLocations(prev => {
            const updated = prev.filter(loc => loc.id_location !== selectedLocationId);
            if (isDebugMode()) {
              logger.log(`[UPDATE] Removendo location ID ${selectedLocationId} da lista. Antes: ${prev.length}, Depois: ${updated.length}`);
            }
            return updated;
          });

          // Limpar seleção
          setAddress('');
          setNotificado('');
          setProtocolo('');
          setSelectedLocationId(null);

          // Segundo reload com delay como backup (se necessário)
          setTimeout(async () => {
            if (isDebugMode()) {
              logger.log('[RELOAD] Fazendo reload adicional após 1s...');
            }
            await loadUserAddresses();
          }, 1000);

          logger.log('Verificação concluída com fallback GPS');
        }
      }
    } catch (err: any) {
      logger.error('❌ Erro na verificação:', err);
      console.error('ERRO COMPLETO:', err); // Log adicional para debug

      // Tratamento mais específico e amigável para erros de geolocalização
      let userFriendlyMessage = '';

      // Primeiro, tenta extrair informações detalhadas do erro
      if (err.userMessage) {
        // Se o erro já tem uma mensagem personalizada, usa ela
        userFriendlyMessage = err.userMessage;
        logger.log('📝 Usando mensagem personalizada do erro');
      } else if (err.code === 4 || err.message === 'MANUAL_INPUT_REQUIRED') {
        userFriendlyMessage = 'Localização automática não disponível. Tente ativar o GPS ou permitir acesso à localização.';
      } else if (err.code === 1 || err.message?.includes('Permission denied') || err.message?.includes('denied')) {
        userFriendlyMessage = 'Permissão de localização negada. Clique no ícone de localização na barra de endereços e permita o acesso.';
      } else if (err.code === 2 || err.message?.includes('Position unavailable')) {
        if (deviceInfo.isDesktop) {
          userFriendlyMessage = 'Localização limitada em computadores. Tente em um dispositivo móvel ou verifique suas configurações de localização.';
        } else {
          userFriendlyMessage = 'Localização indisponível. Verifique se o GPS está ativo e tente novamente.';
        }
      } else if (err.code === 3 || err.message?.includes('timeout') || err.message?.includes('TIMEOUT')) {
        userFriendlyMessage = 'Tempo limite excedido. Verifique sua conexão de internet e tente novamente.';
      } else if (err.message?.includes('HTTPS') || err.message?.includes('secure')) {
        userFriendlyMessage = 'Localização requer conexão segura (HTTPS). Se estiver em desenvolvimento, use localhost.';
      } else if (err.message?.includes('Todas as estratégias falharam')) {
        userFriendlyMessage = 'Não foi possível obter sua localização. Tente:\n• Ativar GPS/Wi-Fi\n• Permitir localização no navegador\n• Verificar conexão de internet\n• Recarregar a página';
      } else if (err.name && err.message) {
        // Se tem nome e mensagem, inclui ambos
        userFriendlyMessage = `Erro: ${err.name} - ${err.message}`;
      } else if (err.message) {
        // Se só tem mensagem
        userFriendlyMessage = `Erro: ${err.message}`;
      } else if (typeof err === 'string') {
        // Se o erro é uma string
        userFriendlyMessage = err;
      } else {
        // Fallback genérico com mais informações
        userFriendlyMessage = 'Erro na obtenção da localização. Detalhes: ' + JSON.stringify(err);
      }

      logger.error('📱 Mensagem para usuário:', userFriendlyMessage);
      setError(userFriendlyMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-4">
      <div className="max-w-md mx-auto">
        {/* Header com informações do usuário */}
        {user && (
          <div className="flex items-center justify-between mb-6 p-4 bg-white rounded-lg shadow-sm">
            <div>
              <h2 className="text-lg font-semibold text-gray-700 flex items-center">
                Olá,
              </h2>
              <h2 className="text-xl font-semibold text-gray-700 flex items-center mb-2" >
                {user.notificador || user.documento}
              </h2>
              <p className="text-sm text-gray-600">
                Bem-vindo !
              </p>
            </div>
            <UserDropdown func={loadUserAddresses} />
          </div>
        )}

        <Card
          variant="elevated"
          padding="lg"
          shadow="lg"
          rounded="lg"
          className="mb-6"
        >
          <h1 className="text-xl text-gray-700 font-bold mb-4 text-center uppercase">CONFIRMAR ENDEREÇO</h1>

          {/* Loading centralizado abaixo do título */}
          {(!addressesLoaded || !modelosLoaded) ? (
            <div className="py-8">
              <Loading
                size="md"
                text="Carregando informações..."
                className="text-center"
              />
            </div>
          ) : (
            <>
              {/* Formulário só aparece após carregamento completo */}
              <Card
                variant="outlined"
                padding="sm"
                rounded="md"
                className="mb-4"
              >
                {availableLocations.length === 0 ? (
                  <div className="w-full p-2 text-gray-500 bg-gray-100 rounded cursor-not-allowed">
                    {addressesLoaded ?
                      "NENHUM ENDEREÇO DISPONÍVEL" :
                      "Carregando..."
                    }
                  </div>
                ) : (
                  <select
                    className="w-full p-2 border-none outline-none bg-transparent cursor-pointer"
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    aria-label="Selecionar endereço para verificar proximidade"
                  >
                    <option value="">Selecione um endereço</option>
                    {availableLocations.map((location) => (
                      <option key={location.id_location} value={location.id_location.toString()}>
                        {location.endereco}
                      </option>
                    ))}
                  </select>
                )}
              </Card>

              {/* Exibir o protocolo quando um endereço for selecionado */}
              {address.trim() && protocolo && (
                <Card
                  variant="filled"
                  background="blue"
                  padding="sm"
                  rounded="md"
                  className="mb-4"
                >
                  <div className="text-white text-center">
                    <span className="text-gray-700 text-sm font-medium">
                      Protocolo
                    </span>
                    <p className="text-gray-700 text-sm font-medium">
                      {protocolo}
                    </p>
                  </div>
                </Card>
              )}

              {/* Exibir o notificado quando um endereço for selecionado */}
              {address.trim() && notificado && (
                <Card
                  variant="filled"
                  background="blue"
                  padding="sm"
                  rounded="md"
                  className="mb-4"
                >
                  <div className="text-white text-center">
                    <span className="text-gray-700 text-sm font-medium">
                      Notificado
                    </span>
                    <p className="text-gray-700 text-sm font-medium">
                      {notificado}
                    </p>
                  </div>
                </Card>
              )}

              {/* Caixa de seleção de Status (POSITIVO/NEGATIVO) */}
              <Card
                variant="outlined"
                padding="sm"
                rounded="md"
                className="mb-4"
              >
                {!address.trim() ? (
                  <div className="w-full p-2 text-gray-500 bg-gray-100 rounded cursor-not-allowed">
                    SELECIONE UM ENDEREÇO PRIMEIRO
                  </div>
                ) : (
                  <select
                    className="w-full p-2 border-none outline-none bg-transparent cursor-pointer"
                    value={statusFilter}
                    onChange={e => {
                      setStatusFilter(e.target.value as 'POSITIVO' | 'NEGATIVO' | '');
                      setSelectedModelo(''); // Reset modelo selection when status changes
                    }}
                    aria-label="Selecionar status do modelo"
                    disabled={!address.trim()}
                  >
                    <option value="">Selecione um status</option>
                    <option value="POSITIVO">POSITIVO</option>
                    <option value="NEGATIVO">NEGATIVO</option>
                  </select>
                )}
              </Card>

              {/* Caixa de seleção de Modelos (filtrada pelo status) */}
              <Card
                variant="outlined"
                padding="sm"
                rounded="md"
                className="mb-4"
              >
                {!address.trim() ? (
                  <div className="w-full p-2 text-gray-500 bg-gray-100 rounded cursor-not-allowed">
                    SELECIONE UM ENDEREÇO PRIMEIRO
                  </div>
                ) : !statusFilter ? (
                  <div className="w-full p-2 text-gray-500 bg-gray-100 rounded cursor-not-allowed">
                    SELECIONE UM STATUS PRIMEIRO
                  </div>
                ) : filteredModelos.length === 0 ? (
                  <div className="w-full p-2 text-gray-500 bg-gray-100 rounded cursor-not-allowed">
                    NENHUM MODELO ENCONTRADO PARA ESTE STATUS
                  </div>
                ) : (
                  <select
                    className="w-full p-2 border-none outline-none bg-transparent cursor-pointer"
                    value={selectedModelo}
                    onChange={e => setSelectedModelo(e.target.value)}
                    aria-label="Selecionar modelo"
                    disabled={!address.trim() || !statusFilter}
                  >
                    <option value="">Selecione um modelo</option>
                    {filteredModelos.map((modelo) => (
                      <option key={modelo.id_modelo} value={modelo.id_modelo.toString()}>
                        {convertISO88591ToUTF8(modelo.modelo)}
                      </option>
                    ))}
                  </select>
                )}
              </Card>

              <button
                className={`w-full p-3 text-white rounded-lg font-medium
                                    upper transition-colors duration-200 
                                    ${isDevEnvironment()
                    ? 'mb-2' : ''} ${loading || !address.trim()
                      || availableLocations.length === 0
                      || !selectedModelo
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                onClick={handleVerify}
                disabled={loading || !address.trim() || availableLocations.length === 0 || !selectedModelo}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Confirmando...</span>
                  </div>
                ) : (
                  'Confirmar'
                )}
              </button>

              {/* Botão de simulação apenas em desenvolvimento */}
              {isDevEnvironment() && (
                <div className="flex gap-2">
                  <button
                    className={`flex-1 p-2 text-sm rounded transition-colors duration-200 flex items-center justify-center gap-2 ${simulationMode
                      ? 'bg-orange-100 text-orange-700 border border-orange-300 hover:bg-orange-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    onClick={() => setSimulationMode(!simulationMode)}
                    type="button"
                  >
                    <MaskIcon size={16} />
                    {simulationMode ? 'Modo Simulação ON' : 'Modo Simulação'}
                  </button>

                  {simulationMode && (
                    <div className="flex-1 text-xs text-orange-600 flex items-center">
                      <span>Para testes locais</span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </Card>

        {/* Resultado simplificado da verificação */}
        {(result || error) && (
          <Card
            variant="filled"
            background={error ? 'red' : 'green'}
            padding="md"
            rounded="lg"
            shadow="md"
            className="mt-4"
          >
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                {error ? (
                  <XIcon size={24} className="text-red-600" />
                ) : (
                  <CheckIcon size={24} className="text-green-600" />
                )}
                <span className="font-medium text-gray-800 text-lg">
                  {error
                    ? "Erro na confirmação!"
                    : "Confirmado com sucesso!"
                  }
                </span>
              </div>

              {/* Mostrar detalhes do erro */}
              {error && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800 text-left whitespace-pre-line">
                    {error}
                  </p>
                </div>
              )}

              <button
                onClick={() => {
                  setResult(null);
                  setError(null);
                }}
                className="mt-2 px-3 py-1 bg-white bg-opacity-80 text-gray-700 rounded text-sm font-medium hover:bg-opacity-100"
              >
                Novo Envio
              </button>
            </div>
          </Card>
        )}

      </div>
    </div>
  );
}
